import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productApi, netValueApi } from '../../services/privateFundApi';
import './NetValueHistory.css';

/**
 * 历史净值管理页面
 */
const NetValueHistory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    netValueDate: '',
    netValue: ''
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [productRes, historyRes] = await Promise.all([
        productApi.getProductById(id),
        netValueApi.getNetValueHistory(id, { page: 1, pageSize: 100 })
      ]);

      if (productRes.success) {
        setProduct(productRes.data);
      }
      if (historyRes.success) {
        setHistory(historyRes.data || []);
      }
    } catch (err) {
      console.error('获取数据失败:', err);
      setError(err.message || '获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/private-fund/products/${id}`);
  };

  const handleAdd = () => {
    setFormData({
      netValueDate: '',
      netValue: ''
    });
    setEditingId(null);
    setShowAddForm(true);
  };

  const handleEdit = (record) => {
    setFormData({
      netValueDate: new Date(record.netValueDate).toISOString().split('T')[0],
      netValue: record.netValue.toString()
    });
    setEditingId(record._id);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({
      netValueDate: '',
      netValue: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        // 更新历史净值
        const response = await netValueApi.updateNetValueHistory(editingId, {
          netValueDate: formData.netValueDate,
          netValue: parseFloat(formData.netValue)
        });
        if (response.success) {
          await fetchData();
          handleCancel();
        } else {
          setError(response.message || '更新失败');
        }
      } else {
        // 添加历史净值
        const response = await netValueApi.addNetValueHistory(id, {
          netValueDate: formData.netValueDate,
          netValue: parseFloat(formData.netValue)
        });
        if (response.success) {
          await fetchData();
          handleCancel();
        } else {
          setError(response.message || '添加失败');
        }
      }
    } catch (err) {
      console.error('操作失败:', err);
      setError(err.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (historyId) => {
    if (!window.confirm('确定要删除这条历史净值记录吗？')) {
      return;
    }

    try {
      setLoading(true);
      const response = await netValueApi.deleteNetValueHistory(historyId);
      if (response.success) {
        await fetchData();
      } else {
        alert(response.message || '删除失败');
      }
    } catch (err) {
      console.error('删除失败:', err);
      alert(err.message || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchImport = () => {
    // TODO: 实现批量导入功能
    alert('批量导入功能开发中...');
  };

  if (loading && !history.length) {
    return (
      <div className="net-value-history">
        <div className="loading-message">加载中...</div>
      </div>
    );
  }

  return (
    <div className="net-value-history">
      <div className="history-header">
        <button className="back-button" onClick={handleBack}>
          ← 返回
        </button>
        <h1 className="history-title">历史净值管理</h1>
      </div>

      {product && (
        <div className="product-info-card">
          <h3>{product.name}</h3>
        </div>
      )}

      <div className="history-content">
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="section-header">
          <h2>历史净值记录</h2>
          <div className="header-actions">
            <button className="action-button" onClick={handleBatchImport}>
              批量导入
            </button>
            <button className="action-button primary" onClick={handleAdd}>
              + 添加历史净值
            </button>
          </div>
        </div>

        {showAddForm && (
          <div className="add-form-card">
            <h3>{editingId ? '编辑历史净值' : '添加历史净值'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="netValueDate">净值日期 *</label>
                  <input
                    type="date"
                    id="netValueDate"
                    name="netValueDate"
                    value={formData.netValueDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, netValueDate: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="netValue">净值 *</label>
                  <input
                    type="number"
                    id="netValue"
                    name="netValue"
                    value={formData.netValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, netValue: e.target.value }))}
                    required
                    min="0.01"
                    step="0.0001"
                    placeholder="请输入净值"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCancel}>
                  取消
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        )}

        {history.length === 0 ? (
          <div className="empty-message">
            <p>暂无历史净值记录</p>
            <button className="action-button" onClick={handleAdd}>
              添加第一条记录
            </button>
          </div>
        ) : (
          <div className="history-table">
            <table>
              <thead>
                <tr>
                  <th>日期</th>
                  <th>净值</th>
                  <th>周收益</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr key={record._id}>
                    <td>{new Date(record.netValueDate).toLocaleDateString()}</td>
                    <td>{record.netValue}</td>
                    <td>
                      {record.weeklyReturn !== null && record.weeklyReturn !== undefined ? (
                        <span className={record.weeklyReturn >= 0 ? 'profit' : 'loss'}>
                          {record.weeklyReturn >= 0 ? '+' : ''}{(record.weeklyReturn * 100).toFixed(2)}%
                        </span>
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEdit(record)}
                        >
                          编辑
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDelete(record._id)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetValueHistory;























